import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { useMessages } from '../../components/Messages';
import { useTranslation } from 'react-i18next';
import { isAdmin as checkAdmin } from '../../utils/roleUtils';
import './HomePage.css';
import { clickable } from '../../utils/a11y';
import EntityLink from '../../components/Common/EntityLink';
import { formatAppDate } from '../../utils/dateUtils';
import ReturnShortagesCard from './ReturnShortagesCard';

const ManagerDashboard = () => {
  const { user } = useAuth();
  const { showError } = useMessages();
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('production');
  const location = useLocation();
  // Details opened from here come back to this dashboard
  const dashboardPath = `${location.pathname}${location.search}`;
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [assignedTo, setAssignedTo] = useState('');
  const [activeTab, setActiveTab] = useState('tasks'); // 'tasks' or 'metrics'
  const [taskFilter, setTaskFilter] = useState('unassigned'); // 'all', 'unassigned', 'urgent', 'overdue'
  const [allTasks, setAllTasks] = useState([]); // Store all tasks for filtering
  const [dashboardData, setDashboardData] = useState({
    departmentStats: null,
    activeTasks: [],
    overdueItems: [],
    teamPerformance: [],
    productionMetrics: {
      daily_output: 0,
      efficiency: 0,
      quality_rate: 0,
      active_orders: 0
    }
  });

  useEffect(() => {
    fetchManagerDashboard();
    fetchUsers();
  }, []);

  useEffect(() => {
    // Update displayed tasks when filter changes
    if (allTasks.length > 0) {
      const filteredTasks = filterTasks(allTasks, taskFilter);
      setDashboardData(prev => ({
        ...prev,
        activeTasks: filteredTasks.slice(0, 5)
      }));
    }
  }, [taskFilter, allTasks]);

  const fetchManagerDashboard = async () => {
    try {
      setLoading(true);
      // Fetch department stats with error handling
      // Managers have cross-department visibility: aggregate all departments
      let deptStats = null;
      try {
        const statsResponse = await apiFetch(getApiUrl('/api/production-tasks/departments/stats'));
        if (statsResponse.ok) {
          const stats = await statsResponse.json();
          if (stats.length > 0) {
            const totalTasks = stats.reduce((sum, d) => sum + (d.total_tasks || 0), 0);
            const completedTasks = stats.reduce((sum, d) => sum + (d.completed_tasks || 0), 0);
            deptStats = {
              total_tasks: totalTasks,
              pending_tasks: stats.reduce((sum, d) => sum + (d.pending_tasks || 0), 0),
              assigned_tasks: stats.reduce((sum, d) => sum + (d.assigned_tasks || 0), 0),
              in_progress_tasks: stats.reduce((sum, d) => sum + (d.in_progress_tasks || 0), 0),
              completed_tasks: completedTasks,
              urgent_tasks: stats.reduce((sum, d) => sum + (d.urgent_tasks || 0), 0),
              overdue_tasks: stats.reduce((sum, d) => sum + (d.overdue_tasks || 0), 0),
              efficiency_percentage: totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0,
            };
          }
        } else {
          console.error('Failed to fetch department stats:', statsResponse.status);
        }
      } catch (error) {
        console.error('Error fetching department stats:', error);
        // Continue without stats - tasks can still be shown
      }

        // Fetch active tasks — managers see all tasks across all departments
        const tasksResponse = await apiFetch(getApiUrl('/api/production-tasks/tasks'));
        const fetchedTasks = tasksResponse.ok ? await tasksResponse.json() : [];

        const relevantTasks = fetchedTasks;

        // Store all relevant tasks for filtering
        setAllTasks(relevantTasks);

        // Apply initial filter
        const activeTasks = filterTasks(relevantTasks, taskFilter);

        // Fetch production orders
        const ordersResponse = await apiFetch(getApiUrl('/api/production/orders?status=RELEASED'));
        const activeOrders = ordersResponse.ok ? await ordersResponse.json() : [];

        setDashboardData({
          departmentStats: deptStats,
          activeTasks: activeTasks.slice(0, 5),
          overdueItems: activeTasks.filter(t => {
            if (!t.scheduled_end) return false;
            return new Date(t.scheduled_end) < new Date() && t.status !== 'COMPLETED';
          }).slice(0, 5),
          teamPerformance: [],
          productionMetrics: {
            daily_output: deptStats?.completed_tasks || 0,
            efficiency: deptStats?.efficiency_percentage || 0,
            quality_rate: 95,
            active_orders: Array.isArray(activeOrders) ? activeOrders.length : 0
          }
        });
    } catch (error) {
      console.error('Error fetching manager dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/users/'));
      if (response.ok) {
        const data = await response.json();
        // Filter users to only show those in the same department
        const departmentUsers = checkAdmin(user)
          ? data // Admins can assign to anyone
          : data.filter(u => u.department_id === user?.department_id);
        setUsers(departmentUsers || []);
      } else {
        showError(tCommon('errors.genericTitle'), tCommon('errors.usersLoadFailed'));
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      showError(tCommon('errors.genericTitle'), tCommon('errors.usersLoadFailed'));
    }
  };

  const filterTasks = (tasks, filter) => {
    const now = new Date();

    switch (filter) {
      case 'unassigned':
        return tasks.filter(t => !t.assigned_to);

      case 'assigned':
        return tasks.filter(t => t.assigned_to);

      case 'urgent':
        return tasks.filter(t => t.priority === 'URGENT' || t.priority === 'HIGH');

      case 'overdue':
        return tasks.filter(t => {
          if (!t.scheduled_end) return false;
          return new Date(t.scheduled_end) < now && t.status !== 'COMPLETED' && t.status !== 'CANCELLED';
        });

      case 'pending':
        return tasks.filter(t => t.status === 'PENDING');

      case 'in_progress':
        return tasks.filter(t => t.status === 'IN_PROGRESS');

      case 'all':
      default:
        return tasks;
    }
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setAssignedTo(task.assigned_to || '');
    setShowAssignModal(true);
  };

  const handleAssignTask = async () => {
    if (!selectedTask || !assignedTo) return;

    try {
      const response = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${selectedTask.task_id || selectedTask.production_task_id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          assigned_to: parseInt(assignedTo)
        })
      });

      if (response.ok) {
        // Refresh the dashboard to show updated assignment
        fetchManagerDashboard();
        setShowAssignModal(false);
        setSelectedTask(null);
        setAssignedTo('');
      }
    } catch (error) {
      console.error('Error assigning task:', error);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toUpperCase()) {
      case 'URGENT': return '#dc3545'; // var(--error)
      case 'HIGH': return '#ffc107';   // var(--warning)
      case 'NORMAL': return '#28a745';  // var(--success)
      case 'LOW': return '#6c757d';     // var(--text-secondary)
      default: return '#999';          // var(--text-secondary)
    }
  };

  const getPriorityClass = (priority) => {
    switch (priority?.toUpperCase()) {
      case 'URGENT': return 'priority-urgent';
      case 'HIGH': return 'priority-high';
      case 'NORMAL': return 'priority-normal';
      case 'LOW': return 'priority-low';
      default: return 'priority-normal';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'COMPLETED': return '#16a34a';  // Green
      case 'IN_PROGRESS': return '#3b82f6'; // Blue
      case 'PENDING': return '#f59e0b';     // Amber
      case 'ASSIGNED': return '#8b5cf6';    // Purple
      case 'CANCELLED': return '#dc2626';   // Red
      default: return '#6b7280';            // Gray
    }
  };

  const getStatusClass = (status) => {
    switch (status?.toUpperCase()) {
      case 'COMPLETED': return 'status-completed';
      case 'IN_PROGRESS': return 'status-in-progress';
      case 'PENDING': return 'status-pending';
      case 'ASSIGNED': return 'status-assigned';
      case 'CANCELLED': return 'status-cancelled';
      default: return 'status-pending';
    }
  };

  if (loading) {
    return (
      <div className="manager-dashboard">
        <div className="loading">{tCommon('homePage.loadingDashboard')}</div>
      </div>
    );
  }

  return (
    <div className="manager-dashboard">
      {/* Tab Navigation */}
      <div className="dashboard-tabs">
        <button
          className={`tab-button ${activeTab === 'tasks' ? 'active' : ''}`}
          onClick={() => setActiveTab('tasks')}
        >
          📋 {t('managerDashboard.tabs.tasks')}
        </button>
        <button
          className={`tab-button ${activeTab === 'metrics' ? 'active' : ''}`}
          onClick={() => setActiveTab('metrics')}
        >
          📊 {t('managerDashboard.tabs.metrics')}
        </button>
      </div>

      {/* Tasks Tab Content */}
      {activeTab === 'tasks' && (
        <div className="tab-content">
          {/* Filter Bar */}
          <div className="filter-bar">
            <div className="filter-buttons">
              <button
                className={`filter-button ${taskFilter === 'all' ? 'active' : ''}`}
                onClick={() => setTaskFilter('all')}
              >
                {t('managerDashboard.filters.all', { count: allTasks.length })}
              </button>
              <button
                className={`filter-button ${taskFilter === 'unassigned' ? 'active' : ''}`}
                onClick={() => setTaskFilter('unassigned')}
              >
                {t('managerDashboard.filters.unassigned', { count: allTasks.filter(task => !task.assigned_to).length })}
              </button>
              <button
                className={`filter-button ${taskFilter === 'assigned' ? 'active' : ''}`}
                onClick={() => setTaskFilter('assigned')}
              >
                {t('managerDashboard.filters.assigned', { count: allTasks.filter(task => task.assigned_to).length })}
              </button>
              <button
                className={`filter-button ${taskFilter === 'urgent' ? 'active' : ''}`}
                onClick={() => setTaskFilter('urgent')}
              >
                {t('managerDashboard.filters.urgent', { count: allTasks.filter(task => task.priority === 'URGENT' || task.priority === 'HIGH').length })}
              </button>
              <button
                className={`filter-button ${taskFilter === 'pending' ? 'active' : ''}`}
                onClick={() => setTaskFilter('pending')}
              >
                {t('managerDashboard.filters.pending', { count: allTasks.filter(task => task.status === 'PENDING').length })}
              </button>
              <button
                className={`filter-button ${taskFilter === 'in_progress' ? 'active' : ''}`}
                onClick={() => setTaskFilter('in_progress')}
              >
                {t('managerDashboard.filters.inProgress', { count: allTasks.filter(task => task.status === 'IN_PROGRESS').length })}
              </button>
              <button
                className={`filter-button ${taskFilter === 'overdue' ? 'active' : ''}`}
                onClick={() => setTaskFilter('overdue')}
              >
                {t('managerDashboard.filters.overdue', { count: allTasks.filter(task => {
                  if (!task.scheduled_end) return false;
                  return new Date(task.scheduled_end) < new Date() && task.status !== 'COMPLETED' && task.status !== 'CANCELLED';
                }).length })}
              </button>
            </div>
          </div>

          <ReturnShortagesCard />

          {/* Active Tasks */}
          <div className="dashboard-section">
            <div className="section-header">
              <h2 className="section-title">
                {taskFilter === 'all' ? t('managerDashboard.sectionTitles.all') :
                 taskFilter === 'unassigned' ? t('managerDashboard.sectionTitles.unassigned') :
                 taskFilter === 'assigned' ? t('managerDashboard.sectionTitles.assigned') :
                 taskFilter === 'urgent' ? t('managerDashboard.sectionTitles.urgent') :
                 taskFilter === 'pending' ? t('managerDashboard.sectionTitles.pending') :
                 taskFilter === 'in_progress' ? t('managerDashboard.sectionTitles.inProgress') :
                 taskFilter === 'overdue' ? t('managerDashboard.sectionTitles.overdue') : t('managerDashboard.tabs.tasks')}
              </h2>
            </div>
            <div className="task-list">
              {dashboardData.activeTasks.length === 0 ? (
                <div className="empty-state">{t('managerDashboard.noActiveTasks')}</div>
              ) : (
                dashboardData.activeTasks.map(task => (
                  <div
                    key={task.task_id}
                    className="task-item"
                    onClick={() => handleTaskClick(task)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="task-header">
                      <span className="task-code">{task.task_code}</span>
                      <span className={`priority-badge ${getPriorityClass(task.priority)}`}>
                        {task.priority}
                      </span>
                    </div>
                    <div className="task-name">{task.task_name}</div>
                    {task.customer_name && (
                      <div className="task-customer-info">
                        <span className="customer-icon">👤</span>
                        <span className="customer-name">{task.customer_name}</span>
                      </div>
                    )}
                    {(task.po_number || task.product_name) && (
                      <div className="task-production-info">
                        {task.po_number && (
                          <span className="po-number">
                            {t('managerDashboard.po')}: <EntityLink type="production-order" id={task.po_number} label={task.po_number} returnLabel={t('managerDashboard.dashboardLabel')} returnPath={dashboardPath} />
                          </span>
                        )}
                        {task.line_number && <span className="line-number">{t('managerDashboard.lineNumber', { number: task.line_number })}</span>}
                        {task.product_code && <span className="product-code">[{task.product_code}]</span>}
                        {task.product_name && <span className="product-name">{task.product_name}</span>}
                      </div>
                    )}
                    <div className="task-footer">
                      <span className={`status-badge ${getStatusClass(task.status)}`}>
                        {task.status}
                      </span>
                      <span className="task-department">
                        {task.department_name || t('managerDashboard.allDepartments')}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Overdue Items */}
          {dashboardData.overdueItems.length > 0 && (
            <div className="dashboard-section alert">
              <div className="section-header">
                <h2 className="section-title">⚠️ {t('managerDashboard.overdueItems')}</h2>
              </div>
              <div className="overdue-list">
                {dashboardData.overdueItems.map(item => (
                  <div key={item.task_id} className="overdue-item">
                    <div className="overdue-header">
                      <span className="task-code">
                        <EntityLink type="task" id={item.task_id} label={item.task_code} returnLabel={t('managerDashboard.dashboardLabel')} returnPath={dashboardPath} />
                      </span>
                      <span className="overdue-time">
                        {t('managerDashboard.due')}: {formatAppDate(item.scheduled_end)}
                      </span>
                    </div>
                    <div className="task-name">{item.task_name}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Metrics Tab Content */}
      {activeTab === 'metrics' && (
        <div className="tab-content">
          {/* Department Overview */}
          <div className="dashboard-section">
            <h2 className="section-title">
              {t('managerDashboard.departmentOverview', { department: user?.department_name || t('managerDashboard.allDepartments') })}
            </h2>
        <div className="metrics-grid">
          <div className="metric-card" {...clickable(() => { setActiveTab('tasks'); setTaskFilter('all'); })}>
            <div className="metric-value">{dashboardData.departmentStats?.total_tasks || 0}</div>
            <div className="metric-label">{t('managerDashboard.metrics.totalTasks')}</div>
            <div className="metric-sublabel">{t('managerDashboard.metrics.allActiveTasks')}</div>
          </div>
          <div className="metric-card" {...clickable(() => { setActiveTab('tasks'); setTaskFilter('pending'); })}>
            <div className="metric-value" style={{ color: '#f39c12' }}>
              {dashboardData.departmentStats?.pending_tasks || 0}
            </div>
            <div className="metric-label">{tCommon('ui.pending')}</div>
            <div className="metric-sublabel">{t('managerDashboard.metrics.awaitingAssignment')}</div>
          </div>
          <div className="metric-card" {...clickable(() => { setActiveTab('tasks'); setTaskFilter('in_progress'); })}>
            <div className="metric-value" style={{ color: '#3498db' }}>
              {dashboardData.departmentStats?.in_progress_tasks || 0}
            </div>
            <div className="metric-label">{tCommon('ui.inProgress')}</div>
            <div className="metric-sublabel">{t('managerDashboard.metrics.currentlyWorking')}</div>
          </div>
          <div className="metric-card" {...clickable(() => { setActiveTab('tasks'); setTaskFilter('all'); })}>
            <div className="metric-value" style={{ color: '#27ae60' }}>
              {dashboardData.departmentStats?.completed_tasks || 0}
            </div>
            <div className="metric-label">{tCommon('ui.completed')}</div>
            <div className="metric-sublabel">{t('managerDashboard.metrics.finishedToday')}</div>
          </div>
          <div className="metric-card urgent" {...clickable(() => { setActiveTab('tasks'); setTaskFilter('urgent'); })}>
            <div className="metric-value" style={{ color: '#e74c3c' }}>
              {dashboardData.departmentStats?.urgent_tasks || 0}
            </div>
            <div className="metric-label">{t('managerDashboard.metrics.urgent')}</div>
            <div className="metric-sublabel">{t('managerDashboard.metrics.highPriority')}</div>
          </div>
          <div className="metric-card" {...clickable(() => { setActiveTab('tasks'); setTaskFilter('overdue'); })}>
            <div className="metric-value" style={{ color: '#e67e22' }}>
              {dashboardData.departmentStats?.overdue_tasks || 0}
            </div>
            <div className="metric-label">{t('managerDashboard.metrics.overdue')}</div>
            <div className="metric-sublabel">{t('managerDashboard.metrics.pastDeadline')}</div>
          </div>
        </div>
      </div>

          {/* Production Metrics */}
          <div className="dashboard-section">
            <h2 className="section-title">{t('managerDashboard.productionMetrics')}</h2>
            <div className="production-metrics">
              <div className="metric-card large">
                <div className="metric-header">
                  <span className="metric-label">{t('managerDashboard.metrics.efficiency')}</span>
                  <span className="metric-trend positive">↑ 5%</span>
                </div>
                <div className="metric-value-large">
                  {dashboardData.productionMetrics.efficiency.toFixed(1)}%
                </div>
                <div className="metric-bar">
                  <div
                    className="metric-bar-fill"
                    style={{ width: `${dashboardData.productionMetrics.efficiency}%` }}
                  />
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-value">{dashboardData.productionMetrics.daily_output}</div>
                <div className="metric-label">{t('managerDashboard.metrics.dailyOutput')}</div>
              </div>
              <div className="metric-card">
                <div className="metric-value">{dashboardData.productionMetrics.quality_rate}%</div>
                <div className="metric-label">{t('managerDashboard.metrics.qualityRate')}</div>
              </div>
              <div className="metric-card">
                <div className="metric-value">{dashboardData.productionMetrics.active_orders}</div>
                <div className="metric-label">{t('managerDashboard.metrics.activeOrders')}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Task Assignment Modal */}
      {showAssignModal && selectedTask && (
        <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('managerDashboard.assign.title')}</h2>
              <button className="modal-close" onClick={() => setShowAssignModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="task-details">
                <h3>{selectedTask.task_code} - {selectedTask.task_name}</h3>
                <p className="task-description">{selectedTask.description}</p>
                {(selectedTask.customer_name || selectedTask.po_number || selectedTask.product_name) && (
                  <div className="task-production-details">
                    {selectedTask.customer_name && (
                      <div className="customer-info">
                        <strong>{tCommon('ui.customer')}:</strong> {selectedTask.customer_name}
                      </div>
                    )}
                    {selectedTask.po_number && (
                      <div className="production-order-info">
                        <strong>{t('managerDashboard.assign.productionOrder')}:</strong> {selectedTask.po_number}
                        {selectedTask.line_number && <span className="line-info"> ({t('managerDashboard.lineNumber', { number: selectedTask.line_number })})</span>}
                      </div>
                    )}
                    {selectedTask.product_name && (
                      <div className="product-info">
                        <strong>{tCommon('ui.product')}:</strong> {selectedTask.product_code && <span className="product-code-detail">[{selectedTask.product_code}]</span>} {selectedTask.product_name}
                      </div>
                    )}
                    {selectedTask.order_quantity && (
                      <div className="quantity-info">
                        <strong>{tCommon('ui.quantity')}:</strong>
                        <span className="quantity-details">
                          {t('managerDashboard.assign.producedOfOrdered', { produced: selectedTask.produced_quantity || 0, ordered: selectedTask.order_quantity })}
                          {selectedTask.produced_quantity && selectedTask.order_quantity && (
                            <span className="progress-percentage"> ({((selectedTask.produced_quantity / selectedTask.order_quantity) * 100).toFixed(0)}%)</span>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                <div className="task-meta-info">
                  <span>{tCommon('ui.priority')}: <strong className={`priority-badge ${getPriorityClass(selectedTask.priority)}`}>{selectedTask.priority}</strong></span>
                  <span>{t('managerDashboard.assign.department')}: <strong>{selectedTask.department_name || t('managerDashboard.allDepartments')}</strong></span>
                  <span>{tCommon('ui.status')}: <strong className={`status-badge ${getStatusClass(selectedTask.status)}`}>{selectedTask.status}</strong></span>
                </div>
              </div>

              <div className="form-group">
                <label>
                  {!checkAdmin(user) ? t('managerDashboard.assign.assignToDepartment', { department: user?.department_name }) : t('managerDashboard.assign.assignTo')}:
                </label>
                <select
                  value={assignedTo}
                  onChange={(e) => setAssignedTo(e.target.value)}
                  className="form-control"
                >
                  <option value="" disabled hidden>{t('managerDashboard.assign.selectUser')}</option>
                  {users.length === 0 ? (
                    <option value="" disabled>{t('managerDashboard.assign.noUsers')}</option>
                  ) : (
                    users.map(user => (
                      <option key={user.user_id} value={user.user_id}>
                        {user.first_name} {user.last_name} ({user.username}) - {user.role || 'USER'}
                      </option>
                    ))
                  )}
                </select>
              </div>

              {selectedTask.assigned_to && (
                <div className="current-assignment">
                  {t('managerDashboard.assign.currentlyAssignedTo')}: {users.find(u => u.user_id === selectedTask.assigned_to)?.username || tCommon('ui.unknown')}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowAssignModal(false)}>
                {tCommon('ui.cancel')}
              </button>
              <button
                className="btn-primary"
                onClick={handleAssignTask}
                disabled={!assignedTo}
              >
                {t('managerDashboard.assign.submit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerDashboard;